import React, { FC, ReactElement, useEffect, useState } from "react";
import classNames from "classnames";
import Tooltip from "../Tooltip/Tooltip.js";
import config from "config";
import { useContent } from "contexts/contentContext/contentContext.js";

import "./Icon.scss";

interface IconSizeI {
  small?: boolean;
  large?: boolean;
  xs?: boolean;
  xl?: boolean;
  xxl?: boolean;
  xxxl?: boolean;
}

interface IconColorI {
  red?: boolean;
  green?: boolean;
  blue?: boolean;
  orange?: boolean;
  white?: boolean;
  gray?: boolean;
  lightgray?: boolean;
  disabledgray?: boolean;
  darkGray?: boolean;
  greenRegular?: boolean;
  pink?: boolean;
}

interface IconI extends IconSizeI, IconColorI {
  name: string;
  src?: string;
  type?: string;
  ext?: "jpeg" | "png" | "gif" | "svg";
  alt?: string;
  title?: string;
  iconClass?: string;
  children?: ReactElement;
  horInvert?: boolean;
  round?: boolean;
  clickable?: boolean;
  disabled?: boolean;
  medium?: boolean;
  dot?: boolean;
  onClick?: any;
  showTitle?: boolean;
  img?: {
    src: string;
    title: string;
    alt: string;
  };
}

interface IconWithTooltipI {
  iconProps: IconI;
  tooltipProps: any;
}

const Icon: FC<IconI> = ({
  name = "",
  src,
  type,
  ext = "svg",
  alt = "",
  title = "",
  iconClass = "",
  children,
  showTitle,
  ...rest
}) => {
  const { getTextByKey } = useContent();
  const classes = getClassName(rest, iconClass);
  const {
    small,
    large,
    xl,
    xxl,
    xxxl,
    blue,
    red,
    white,
    orange,
    gray,
    green,
    disabledgray,
    lightgray,
    darkGray,
    horInvert,
    round,
    clickable,
    medium,
    dot,
    ...props
  } = rest;

  const [outerSrc, setOuterSrc] = useState("");

  useEffect(() => {
    if (name) {
      const imgPath = `${config.api.resource}/assets/icons/${name}.${ext}`;
      setOuterSrc(imgPath);
    } else {
      src && setOuterSrc(src);
    }
  }, [name]);

  const handleImageError = () => {
    if (src) {
      setOuterSrc(src);
    }
  };

  const getIconTitle = () => {
    if (name && showTitle) {
      const fileName = name.split("/").pop();
      return getTextByKey("image-description-" + fileName);
    }
    return "";
  };

  return outerSrc && rest.dot ? (
    <div className="icon-dot-container">
      <img
        src={outerSrc}
        alt={name}
        {...{ ...props }}
        className={classes}
        onError={handleImageError}
      />
    </div>
  ) : outerSrc ? (
    <img
      src={outerSrc}
      alt={getIconTitle()}
      title={getIconTitle()}
      {...{ ...props }}
      className={classes}
      onError={handleImageError}
    />
  ) : null;
};

function getClassName(props: Partial<IconI>, iconClass: string) {
  const classes = ["jm-icon"];
  if (props.disabled) classes.push("disabled-icon");
  if (props.xs) classes.push("xs-icon");
  if (props.small) classes.push("small-icon");
  if (props.large) classes.push("large-icon");
  if (props.xl) classes.push("xl-icon");
  if (props.blue) classes.push("blue-icon");
  if (props.green) classes.push("green-icon");
  if (props.red) classes.push("red-icon");
  if (props.white) classes.push("white-icon");
  if (props.gray) classes.push("gray-icon");
  if (props.lightgray) classes.push("light-gray-icon");
  if (props.darkGray) classes.push("dark-gray-icon");
  if (props.pink) classes.push("pink-icon");
  if (props.disabledgray) classes.push("disabled-gray-icon");
  if (props.greenRegular) classes.push("green-regular-icon");
  if (props.orange) classes.push("orange-icon");
  if (props.round) classes.push("round-icon");
  if (props.round) classes.push("dot-icon");
  if (props.xxl) classes.push("xxl-icon");
  if (props.xxxl) classes.push("xxxl-icon");
  if (props.clickable) classes.push("clickable-icon");
  if (props.medium) classes.push("medium-icon");
  if (props.horInvert) classes.push("horizontal-invert");
  if (iconClass) classes.push(iconClass);
  return classNames(classes);
}

export const IconWithTooltip: FC<IconWithTooltipI> = ({
  iconProps,
  tooltipProps,
}) => {
  return (
    <Tooltip {...tooltipProps}>
      <Icon {...iconProps} />
    </Tooltip>
  );
};

export default Icon;
